import React from "react";
import DomainThreadScore from "./DomainThreadScore";
import PreviewCard from "./PreviewCard";
import PercentileBox from "./PercentileBox";

import BatchPrintButton from "../../blocks/Button/BatchPrintButton"

import useReportSelections from "../../util/useReportSelections";
import Tooltip from "rc-tooltip";
import rope from "../ReadingRope/rope.png"

import Legend from "Components/Teacher/Legend"
import getPercentileColor from "../../util/getPercentileColor";
import { GetBrandedAppName } from "../../../../services/BrandingService";

// componentized parent report content

export default function Content(props) {
  const {
    student,
    readingRopeReport,
    classroom,
    navigation,
    setSkillModalData,
  } = props;

  const selections = useReportSelections();
  const {selectedStatePercentileCuts} = selections;

  function parseData() {
    const data = readingRopeReport
    let outData = adjustPace(data);
    outData = validatePercentiles(outData);
    let overall = outData.mastery;
    let comprehension = outData.threads.find(thread=>thread.label==="Comprehension");
    let word_recognition = outData.threads.find(thread=>thread.label==="Word Recognition");
    overall.armScore = addScore(overall);

    return {
      vocabulary: comprehension.domains.find(domain=>domain.label==="Vocabulary"),
      world_knowledge: comprehension.domains.find(domain=>domain.label==="Background Knowledge"),
      structures_reasoning: comprehension.domains.find(domain=>domain.label==="Structure & Reasoning"),
      phonological_awareness: word_recognition.domains.find(domain=>domain.label==="Phonological Awareness"),
      decoding: word_recognition.domains.find(domain=>domain.label==="Decoding"),
      sight_recognition: { ...word_recognition.domains.find(domain=>domain.label==="Sight Recognition"), label: 'High Frequency Words' },
      mastery: {
        overall,
        comprehension: comprehension.mastery,
        word_recognition: word_recognition.mastery,
      },
    };
  }

  // skip rendering if no data
  if (!readingRopeReport) return null;
  if (readingRopeReport && readingRopeReport.data && readingRopeReport.data.length === 0)
    return null;

  return (
    <div className="instructionalReport">
      {student && <div className="instructional-title">{student.label} in {classroom.label}</div>}
      <div className="content">
        {renderTopHeaders()}
        <div className="reading-rope-container">
          <img
            src={rope}
            alt="Reading Rope"
            className="rope-image"
            style={{ opacity: readingRopeReport ? 1 : 0.5 }}
            />
          {renderDomainThreadScores()}
          {renderOverallScores()}
          {student && renderPreviewCards()}
          <div className="reading-rope-batch-print">
            <BatchPrintButton />
          </div>
          {selectedStatePercentileCuts && renderLegend()}
        </div>
      </div>
    </div>
  );

  function renderTopHeaders() {
    return (
      <div className="top-headers-container">
        <div className="top-header-text">Current Mastery</div>
        <div className="top-header-text">
        <div className="top-header-text">What's Next</div>
          {/* {!studentId && classroom ? `${classroom}` : "What's Next"} */}
        </div>
        <div className="top-header-text">To Improve...</div>
      </div>
    );
  }

  function renderDomainThreadScores() {
    const d = parseData();

    return (
      <React.Fragment>
        <div className="domain-thread-cards" style={{ top: "6%" }}>
          <DomainThreadScore
            title="Vocabulary"
            barColor="var(--vocabulary)"
            data={d.vocabulary}
            onClick={() => navigation("VOCABULARY")}
          />
          <DomainThreadScore
            title="Background Knowledge"
            barColor="var(--background-knowledge)"
            data={d.world_knowledge}
            onClick={() => navigation("WORLD_KNOWLEDGE")}
          />
          <Tooltip
            placement="bottom"
            overlayStyle={{ width: 200, background: "white" }}
            overlay={
              <p style={{ textAlign: "center" }}>
                {GetBrandedAppName()} is not yet calculating data on Structures & Reasoning.{" "}
                <br /> This is coming soon.
              </p>
            }
          >
            <DomainThreadScore
              title="Structures&nbsp;& Reasoning"
              barColor="var(--structures-reasoning)"
              data={d.structures_reasoning}
            />
          </Tooltip>
        </div>
        <div className="domain-thread-cards" style={{ top: "55%" }}>
          <DomainThreadScore
            title="Phonological Awareness"
            barColor="var(--phonological-awareness)"
            data={d.phonological_awareness}
            onClick={() => navigation("PHONEMIC_AWARENESS")}
          />
          <DomainThreadScore
            title="Decoding"
            barColor="var(--decoding)"
            data={d.decoding}
            onClick={() => navigation("DECODING")}
          />
          <DomainThreadScore
            title="High Frequency Words"
            barColor="var(--sight-recognition)"
            data={d.sight_recognition}
            onClick={() => navigation("SIGHT_RECOGNITION")}
          />
        </div>
      </React.Fragment>
    );
  }

  function renderOverallScores() {
    const d = parseData();
    const armScore = d.mastery.overall.armScore.value;
    const showArmScore = armScore || armScore == 0;

    const width = 80;
    const textStyle = { fontSize: 40, fontWeight: "600", textAlign: "center" };
    return (
      <div className="right-thread-cards">
        <div className="right-thread-card hover-opacity">
          <div className="overall-container">Comprehension</div>
          <PercentileBox
            mastery={d.mastery.comprehension}
            textStyle={textStyle}
            boxStyle={{ width }}
            onClick={() => navigation("PHONEMIC_AWARENESS")}
          />
        </div>

        <div className="right-thread-card hover-opacity">
          <div className="overall-container">{armScore ? "Percentile" : "Overall Mastery"}</div>
          <PercentileBox
            mastery={d.mastery.overall}
            textStyle={textStyle}
            boxStyle={{ width }}
            onClick={() => navigation("PHONEMIC_AWARENESS")}
          />
          {showArmScore && <div className="percentile-box-small"><PercentileBox
            mastery={d.mastery.overall.armScore}
            boxStyle={{ width }}
            onClick={() => navigation("PHONEMIC_AWARENESS")}
          /></div> }
          {showArmScore && <div className="overall-container">ARM Score</div>}
        </div>
        <div className="right-thread-card hover-opacity">
          <div className="overall-container">Word Recognition</div>
          <PercentileBox
            mastery={d.mastery.word_recognition}
            textStyle={textStyle}
            boxStyle={{ width }}
            onClick={() => navigation("PHONEMIC_AWARENESS")}
          />
        </div>
      </div>
    );
  }

  function renderPreviewCards() {
    const d = parseData();

    return (
      <React.Fragment>
        <div className="preview-cards" style={{ top: "4%" }}>
          <PreviewCard
            title="Vocabulary"
            borderColor="var(--vocabulary)"
            data={d.vocabulary}
            onClick={() => navigation("VOCABULARY")}
            setSkillModalData={setSkillModalData}
          />
          <PreviewCard
            title="Background Knowledge"
            borderColor="var(--background-knowledge)"
            data={d.world_knowledge}
            onClick={() => navigation("WORLD_KNOWLEDGE")}
            setSkillModalData={setSkillModalData}
          />
          <PreviewCard
            title="Structures & Reasoning"
            borderColor="var(--structures-reasoning)"
            data={d.structures_reasoning}
            disabled={true}
            setSkillModalData={setSkillModalData}
          />
        </div>
        <div className="preview-cards" style={{ top: "50%" }}>
          <PreviewCard
            title="Phonological Awareness"
            borderColor="var(--phonological-awareness)"
            data={d.phonological_awareness}
            onClick={() => navigation("PHONEMIC_AWARENESS")}
            setSkillModalData={setSkillModalData}
          />
          <PreviewCard
            title="Decoding"
            borderColor="var(--decoding)"
            data={d.decoding}
            onClick={() => navigation("DECODING")}
            setSkillModalData={setSkillModalData}
          />
          <PreviewCard
            title="High Frequency Words"
            borderColor="var(--sight-recognition)"
            data={d.sight_recognition}
            onClick={() => navigation("SIGHT_RECOGNITION")}
            setSkillModalData={setSkillModalData}
          />
        </div>
      </React.Fragment>
    );
  }

  function renderLegend() {
    const percentilesLegendItems = [
      {
        blockColor: getPercentileColor("BELOW_GRADE_LEVEL"),
        contents: <div>Tier 3</div>
      },
      {
        blockColor: getPercentileColor("AT_GRADE_LEVEL"),
        contents: <div>Tier 2</div>
      },
      {
        blockColor: getPercentileColor("DIFFERENTIATION_NEEDED"),
        contents: <div>Tier 1 with differentiation</div>
      },
      {
        blockColor: getPercentileColor("AHEAD_OF_GRADE_LEVEL"),
        contents: <div>Tier 1</div>
      }
    ]

    return (
      <div className='reading-rope-legend-container'>
        <Legend items={percentilesLegendItems} direction={"row"}/>
      </div>
    )
  }

  function adjustPace(ropeData){
    let returnValue = JSON.parse(JSON.stringify(ropeData));
    if(selectedStatePercentileCuts){
      returnValue.mastery.pacing = calculateRAG(ropeData.mastery.value);
      returnValue.threads = returnValue.threads.map(thread=>{
        thread.mastery.pacing = calculateRAG(thread.mastery.value);
        thread.domains = thread.domains.map(domain=>{
          domain.mastery.pacing = calculateRAG(domain.mastery.value);
          return domain;
        })
        return thread;
      });
    }
    return returnValue;
  }

  function calculateRAG(score){
    if(score === null || score === undefined || !selectedStatePercentileCuts) return;
    if(score >= selectedStatePercentileCuts.cutscores.green) return "AHEAD_OF_GRADE_LEVEL";
    if(selectedStatePercentileCuts.cutscores.blue && score >= selectedStatePercentileCuts.cutscores.blue) return "DIFFERENTIATION_NEEDED";
    if(score >= selectedStatePercentileCuts.cutscores.yellow) return "AT_GRADE_LEVEL";
    return "BELOW_GRADE_LEVEL";
  }

  function validatePercentiles(inData){
    let outData = inData;
    outData.mastery.value = adjustPercentile(outData.mastery.value);
    outData.threads = outData.threads.map(thread=>{
      thread.mastery.value = adjustPercentile(thread.mastery.value);
      thread.domains = thread.domains.map(domain=>{
        domain.mastery.value = adjustPercentile(domain.mastery.value);
        return domain;
      });
      return thread;
    });
    return outData;
  }

  function adjustPercentile(value){
    //Don't touch null or undefined
    if(!value && value !== 0){
      return value;
    }

    let outValue = value;
    outValue = Math.round(outValue);

    //Make sure our floor is 1
    return outValue === 0 ? 1 : outValue;
  }

  function addScore(mastery){
    let score = (mastery && mastery.meta) ? mastery.meta.srcScoreValue : null;
    if(score){
      score = parseFloat(score).toFixed(2);
    }
    return {value: score, pacing: mastery.pacing};
  }
}
